/**
 * 廣告活動營運頁：安全繁體中文錯誤文案（不顯示 Axios／stack／內部 URL）。
 * 僅供 AdsCampaignOpsPage 使用，不新增 list API 語意。
 */

export const CAMPAIGN_OPS_ERROR_MESSAGES = {
  blankId: '請輸入廣告活動 ID',
  invalidFormat: '廣告活動 ID 格式不正確',
  http400: '無法查詢此廣告活動，請確認活動 ID 是否正確',
  http401: '登入狀態已失效，請重新登入',
  http403: '你沒有查看或操作此廣告活動的權限',
  http404: '找不到這筆廣告活動',
  http409: '廣告活動狀態已更新，請重新查詢後再操作',
  http429: '操作過於頻繁，請稍後再試',
  http5xxLookup: '系統暫時無法查詢廣告活動，請稍後再試',
  http5xxSuspend: '系統暫時無法強制暫停廣告活動，請稍後再試',
  http5xxRelease: '系統暫時無法解除暫停，請稍後再試',
  network: '目前無法連線至服務，請檢查網路後重試',
  unknownLookup: '查詢廣告活動時發生問題，請稍後再試',
  unknownSuspend: '強制暫停失敗，請稍後再試',
  unknownRelease: '解除暫停失敗，請稍後再試',
  /** 動作失敗時的 400（非查詢） */
  http400Action: '無法完成此操作，請確認活動狀態後重試',
};

/**
 * 活動 ID 為 Snowflake 數字字串（不接受英數混雜或符號）。
 * @param {string} raw
 * @returns {boolean}
 */
export function isValidCampaignIdFormat(raw) {
  const id = String(raw || '').trim();
  if (!id) return false;
  return /^\d{1,30}$/.test(id);
}

function readHttpStatus(err) {
  // createClient 的 ApiError 使用 httpStatus；Axios 原生為 response.status
  const status = err?.httpStatus ?? err?.response?.status ?? err?.status;
  if (typeof status === 'number' && status > 0) return status;
  return null;
}

function isNetworkOrTimeout(err) {
  if (!err) return false;
  if (err.response) return false;
  const code = String(err.networkCode || err.code || '');
  const msg = String(err.message || '');
  // ApiError.networkCode／Axios code
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
  if (/^Network Error$/i.test(msg)) return true;
  if (/timeout/i.test(msg)) return true;
  // 無 HTTP status 且無業務 code 時，也可能是斷線
  if (err.response == null && err.httpStatus == null && err.status == null && !err.code && /^Network/i.test(msg)) {
    return true;
  }
  return false;
}

/**
 * @param {unknown} err
 * @param {'lookup'|'suspend'|'release'} kind
 * @returns {string}
 */
export function resolveAdsCampaignOpsError(err, kind = 'lookup') {
  if (isNetworkOrTimeout(err)) {
    return CAMPAIGN_OPS_ERROR_MESSAGES.network;
  }

  const status = readHttpStatus(err);
  const businessCode = typeof err?.code === 'number' ? err.code : null;

  // 業務碼優先於「HTTP 200 + code≠200」包裝
  if (businessCode === 90005 || businessCode === 90003) {
    return CAMPAIGN_OPS_ERROR_MESSAGES.http401;
  }

  if (status === 401) return CAMPAIGN_OPS_ERROR_MESSAGES.http401;
  if (status === 403) return CAMPAIGN_OPS_ERROR_MESSAGES.http403;
  if (status === 404) return CAMPAIGN_OPS_ERROR_MESSAGES.http404;
  if (status === 409 || businessCode === 95903) return CAMPAIGN_OPS_ERROR_MESSAGES.http409;
  if (status === 429) return CAMPAIGN_OPS_ERROR_MESSAGES.http429;

  if (status === 400) {
    return kind === 'lookup'
      ? CAMPAIGN_OPS_ERROR_MESSAGES.http400
      : CAMPAIGN_OPS_ERROR_MESSAGES.http400Action;
  }

  if (status != null && status >= 500) {
    if (kind === 'suspend') return CAMPAIGN_OPS_ERROR_MESSAGES.http5xxSuspend;
    if (kind === 'release') return CAMPAIGN_OPS_ERROR_MESSAGES.http5xxRelease;
    return CAMPAIGN_OPS_ERROR_MESSAGES.http5xxLookup;
  }

  // Gateway／BFF 常見：HTTP 200 + body.code ≠ 200（非成功）
  if (businessCode != null && businessCode !== 200 && (status === 200 || status == null)) {
    return kind === 'lookup'
      ? CAMPAIGN_OPS_ERROR_MESSAGES.http400
      : CAMPAIGN_OPS_ERROR_MESSAGES.http400Action;
  }

  if (kind === 'suspend') return CAMPAIGN_OPS_ERROR_MESSAGES.unknownSuspend;
  if (kind === 'release') return CAMPAIGN_OPS_ERROR_MESSAGES.unknownRelease;
  return CAMPAIGN_OPS_ERROR_MESSAGES.unknownLookup;
}
